/**
 * Multilingual font support for jsPDF.
 * Lazy-loads Noto Sans fonts for Indic scripts on demand and applies the
 * right font per text fragment.
 *
 * Usage:
 *   import { setupMultilingualPDF, mlText, mlSplitTextForAutoTable } from '../utils/pdfMultilingual';
 *
 *   const doc = new jsPDF(...);
 *   await setupMultilingualPDF(doc); // preload all required fonts (lazy, ~720KB total)
 *
 *   mlText(doc, 'தண்ணீர் பாட்டில் Water Bottle', 10, 20); // auto-picks font per script
 */

// Script Unicode ranges
const SCRIPT_RANGES = [
  { name: 'tamil',       fontKey: 'NotoSansTamil',       url: '/fonts/NotoSansTamil-Regular.ttf',       test: /[\u0B80-\u0BFF]/ },
  { name: 'devanagari',  fontKey: 'NotoSansDevanagari',  url: '/fonts/NotoSansDevanagari-Regular.ttf',  test: /[\u0900-\u097F]/ },
  { name: 'telugu',      fontKey: 'NotoSansTelugu',      url: '/fonts/NotoSansTelugu-Regular.ttf',      test: /[\u0C00-\u0C7F]/ },
  { name: 'kannada',     fontKey: 'NotoSansKannada',     url: '/fonts/NotoSansKannada-Regular.ttf',     test: /[\u0C80-\u0CFF]/ },
  { name: 'malayalam',   fontKey: 'NotoSansMalayalam',   url: '/fonts/NotoSansMalayalam-Regular.ttf',   test: /[\u0D00-\u0D7F]/ },
];

// Memo: base64 font data once fetched
const _fontCache = {};

const arrayBufferToBase64 = (buf) => {
  const bytes = new Uint8Array(buf);
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
};

const loadFontBase64 = async (url) => {
  if (_fontCache[url]) return _fontCache[url];
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Font load failed: ${url}`);
  const buf = await res.arrayBuffer();
  const b64 = arrayBufferToBase64(buf);
  _fontCache[url] = b64;
  return b64;
};

// Detect which scripts appear in given text. Returns array of script names.
export const detectScripts = (text = '') => {
  const found = new Set();
  for (const s of SCRIPT_RANGES) {
    if (s.test.test(text)) found.add(s.name);
  }
  return Array.from(found);
};

// Setup: scans an array of strings (e.g. all bill items text), pre-loads required fonts.
// Falls back gracefully if a script font fails — text in that script will render as default.
export const setupMultilingualPDF = async (doc, samples = []) => {
  const text = samples.filter(Boolean).join(' ');
  const scripts = detectScripts(text);
  const tasks = [];
  for (const s of SCRIPT_RANGES) {
    if (scripts.includes(s.name)) {
      tasks.push((async () => {
        try {
          const b64 = await loadFontBase64(s.url);
          doc.addFileToVFS(`${s.fontKey}.ttf`, b64);
          doc.addFont(`${s.fontKey}.ttf`, s.fontKey, 'normal');
        } catch (e) {
          console.warn(`[pdfMultilingual] Failed to register ${s.fontKey}:`, e);
        }
      })());
    }
  }
  await Promise.all(tasks);
};

// Set the appropriate font on doc for a given text fragment based on its dominant script.
// Returns the font key that was applied (so callers can restore later if needed).
export const applyFontForText = (doc, text = '', defaultFont = 'helvetica') => {
  for (const s of SCRIPT_RANGES) {
    if (s.test.test(text)) {
      try {
        doc.setFont(s.fontKey, 'normal');
        return s.fontKey;
      } catch (_) {
        // Font not loaded — fall back to default
      }
    }
  }
  doc.setFont(defaultFont);
  return defaultFont;
};

// Write text with auto-detected font.
// For full mixed-script support across one line, this picks the font of the FIRST detected script.
// If the line has 100% Latin, it stays on the default font.
export const mlText = (doc, text, x, y, options = {}) => {
  const prevFont = doc.getFont();
  applyFontForText(doc, String(text || ''));
  doc.text(String(text || ''), x, y, options);
  // Restore default font for subsequent calls
  try { doc.setFont(prevFont.fontName || 'helvetica', prevFont.fontStyle || 'normal'); } catch (_) { doc.setFont('helvetica'); }
};

// For jspdf-autotable: returns a styles object that picks the right font per cell
// based on the cell's text content. Use in `didParseCell` hook.
export const autoTableMultilingualHook = (data) => {
  const text = (data.cell.raw || data.cell.text || '').toString();
  for (const s of SCRIPT_RANGES) {
    if (s.test.test(text)) {
      data.cell.styles.font = s.fontKey;
      return;
    }
  }
};
